const backfillMultipleReceiptsFix = async (db: IDBDatabase) => {
  const tx = db.transaction(["pendingInvoices", "pos"], "readwrite");

  // get pos reference
  const posObjectStore = tx.objectStore("pos");
  const posRequest = posObjectStore.getAll();

  const pos: PosSchema = await new Promise((resolve, reject) => {
    posRequest.onsuccess = (event) => {
      const data = (event.target as any).result?.[0] as PosSchema;

      if (data) {
        resolve(data);
      } else {
        reject(new Error("Failed to get pos"));
      }
    };

    posRequest.onerror = () => {
      reject(new Error("Failed to get pos"));
    };
  });

  const posReference = { ...pos.reference };

  // backfill multiple receipts fix for pending invoices
  const pendingInvoicesObjectStore = tx.objectStore("pendingInvoices");
  const pendingInvoiceRequest = pendingInvoicesObjectStore.openCursor();

  await new Promise((resolve, reject) => {
    pendingInvoiceRequest.onsuccess = (event) => {
      const cursor = (event.target as any).result as IDBCursorWithValue;
      if (!cursor) {
        resolve(null);
        return;
      }

      const invoice = cursor.value as PendingInvoiceSchema;

      // Skip if no receipt usages or just one receipt
      if (
        !invoice.salesReceiptInvoiceUsages?.length ||
        invoice.salesReceiptInvoiceUsages.length <= 1
      ) {
        cursor.continue();
        return;
      }

      // Find duplicated references by counting occurrences
      const referenceCount = invoice.salesReceiptInvoiceUsages.reduce(
        (acc, usage) => {
          const ref = usage.salesReceipt?.reference;
          if (ref) {
            acc[ref] = (acc[ref] || 0) + 1;
          }
          return acc;
        },
        {} as Record<string, number>
      );

      // Find references that appear more than once
      const duplicatedReferences = Object.entries(referenceCount)
        .filter(([_, count]) => count > 1)
        .map(([ref]) => ref);

      if (duplicatedReferences.length === 0) {
        cursor.continue();
        return;
      }

      // Process each duplicated reference
      const updatedUsages = invoice.salesReceiptInvoiceUsages.map(
        (usage, index) => {
          const ref = usage.salesReceipt?.reference;
          if (!ref || !duplicatedReferences.includes(ref)) {
            return usage;
          }

          // Keep original reference for the first occurrence of each duplicated reference
          const previousIndex = invoice.salesReceiptInvoiceUsages!.findIndex(
            (u) => u.salesReceipt?.reference === ref
          );

          if (previousIndex === index) {
            return usage;
          }

          // Generate new reference for duplicates
          const reference = generateReference({
            referenceData: posReference,
            transactionDate: invoice.issueDate,
            keyPath: "posReceipts",
          });

          posReference.posReceiptsReferenceCounter += 1;

          return {
            ...usage,
            salesReceipt: {
              ...usage.salesReceipt,
              reference,
            },
          };
        }
      );

      cursor.update({
        ...invoice,
        salesReceiptInvoiceUsages: updatedUsages,
      });

      cursor.continue();
    };

    pendingInvoiceRequest.onerror = () => {
      reject(new Error("Failed to iterate over pending invoices"));
    };
  });

  // update the pos reference
  const posUpdateRequest = posObjectStore.put(
    {
      ...pos,
      reference: posReference,
    },
    pos.id
  );

  await new Promise((resolve, reject) => {
    posUpdateRequest.onsuccess = () => {
      resolve(null);
    };

    posUpdateRequest.onerror = () => {
      reject(new Error("Failed to update pos reference"));
    };
  });
};

export default backfillMultipleReceiptsFix;
